import { useMutation, useSubscription } from '@apollo/client';
import getRedirectQueryString from './getRedirectQueryString';
import { SessionActions } from './sessionSlice';
import getReduxStore from '../app/redux/getReduxStore';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import { isElectron } from '../utils/desktopClient';
import useIsPublicView from '../utils/useIsPublicView';

const SESSION_LOGOUT_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription sessionLogout {
    sessionLogout
  }
`);

const LOGOUT_MUTATION = gql(/** @complexity 2 */ `
  mutation logout {
    logout
  }
`);

const localLogout = ({
  includeRedirectQueryString = true,
  preventRedirect,
}: {
  includeRedirectQueryString?: boolean;
  preventRedirect?: boolean;
} = {}) => {
  const { pathname } = document.location;
  getReduxStore().dispatch(SessionActions.clearSession());
  if (pathname !== '/login' && !preventRedirect) {
    if (isElectron) {
      window.desktop?.logout?.();
    } else if (pathname !== '/deleted') {
      const redirectSearch = includeRedirectQueryString
        ? getRedirectQueryString()
        : '';

      document.location.href = `/login${redirectSearch}`;
    }
  }
};

export const useSessionLogoutSubscription = () => {
  const isLoggedIn = useAppSelector((state) => !!state.session.user.id);
  const isPublicView = useIsPublicView();

  useSubscription(SESSION_LOGOUT_SUBSCRIPTION, {
    skip: !isLoggedIn || isPublicView,
    onData: () => localLogout(),
  });
};

export const useLogout = (options: Parameters<typeof localLogout>[0] = {}) => {
  const [logoutMutate] = useMutation(LOGOUT_MUTATION);

  const handleLogout = () => {
    logoutMutate().finally(() => localLogout(options));
  };

  return handleLogout;
};

export default localLogout;
